import React, { useMemo, useState } from 'react';
import { getConfig } from '@edx/frontend-platform';
import { useQuery } from '@tanstack/react-query';
import { fetchLearnerHomeInit } from '@blend-ed/blendxplatform/api';
import { Toast } from '@openedx/paragon';
import { useIntl } from '@edx/frontend-platform/i18n';

import { useContentRatings } from '../../../gql/hooks/useContentRatings';
import { useCourseTagsBatch } from '../../../gql/hooks/useCourseTagsBatch';
import { useCoursesEffort } from '../../discover/useCoursesEffort';
import { formatMinutes } from '../../discover/courseDataParser';
import {
  formatStartDate,
  daysUntil,
  computeIsLocked,
} from '../../courses/startDateUtils';
import coursesMessages from '../../courses/messages';
import { useProgramProgress } from '../useProgramProgress';
import ProgramBanner from './ProgramBanner';
import ProgramProgressCard from './ProgramProgressCard';
import ProgramAbout from './ProgramAbout';
import EnrolledProgramCourses from './EnrolledProgramCourses';

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1515879218367-8466d910aaa4?auto=format&fit=crop&w=1200&q=60';

const resolveImage = (path) => {
  if (!path) return FALLBACK_IMAGE;
  return path.startsWith('http') ? path : `${getConfig().LMS_BASE_URL}${path}`;
};

/**
 * Build { [openedxCourseId]: { title, imageUrl, homeUrl } } from the
 * learner-home-init payload — the same source the old MFE used to render
 * the courses inside a program (frontend-app-dashboard's LearnerProgram).
 */
const buildCourseInfo = (homeInit) => {
  const lms = getConfig().LMS_BASE_URL;
  const result = {};
  (homeInit?.courses || []).forEach((entry) => {
    const courseId = entry?.courseRun?.courseId;
    if (!courseId) return;
    const resume = entry?.courseRun?.resumeUrl;
    result[courseId] = {
      title: entry?.course?.courseName || null,
      imageUrl: entry?.course?.bannerImgSrc ? `${lms}${entry.course.bannerImgSrc}` : null,
      homeUrl: resume ? `${lms}${resume}` : (entry?.courseRun?.homeUrl || null),
      startDate: entry?.courseRun?.startDate || null,
      isLocked: computeIsLocked(entry?.courseRun),
    };
  });
  return result;
};

const EnrolledProgramView = ({ program }) => {
  const intl = useIntl();
  const [toast, setToast] = useState(null);
  const programCourses = useMemo(
    () => (program.programCourses || []).filter((entry) => entry?.course),
    [program.programCourses],
  );

  const openedxCourseIds = useMemo(
    () => programCourses.map((e) => e.course.openedxCourseId).filter(Boolean),
    [programCourses],
  );
  const contentIds = useMemo(
    () => programCourses.map((e) => e.course.id).filter(Boolean),
    [programCourses],
  );

  const { data: homeInit } = useQuery({
    queryKey: ['learnerHomeInit'],
    queryFn: fetchLearnerHomeInit,
  });
  const { effortByCourseId } = useCoursesEffort(openedxCourseIds);
  const { ratingsByContentId } = useContentRatings(contentIds);
  const { tagsByCourseId } = useCourseTagsBatch(openedxCourseIds);
  const { progressByCourseId } = useProgramProgress();

  const courseInfoById = useMemo(() => buildCourseInfo(homeInit), [homeInit]);

  const courses = useMemo(() => programCourses.map((entry) => {
    const { course } = entry;
    const openedxCourseId = course.openedxCourseId;
    const info = courseInfoById[openedxCourseId] || {};
    const percent = Math.round(progressByCourseId[openedxCourseId] ?? 0);
    let status = null;
    if (percent >= 100) status = 'completed';
    else if (percent > 0) status = 'in-progress';

    const rating = ratingsByContentId[course.id];
    const totalMinutes = effortByCourseId[openedxCourseId]?.total_minutes;

    return {
      contentId: course.id || openedxCourseId,
      title: info.title || openedxCourseId || '',
      imageUrl: resolveImage(info.imageUrl),
      duration: formatMinutes(totalMinutes),
      rating: rating?.avgRating != null && rating.reviewCount > 0
        ? Number(rating.avgRating.toFixed(1))
        : null,
      reviewCount: rating?.reviewCount ?? 0,
      skills: tagsByCourseId[openedxCourseId] || [],
      percent,
      status,
      isLocked: info.isLocked || false,
      startDate: info.startDate || null,
      homeUrl: info.homeUrl
        || (openedxCourseId
          ? `${getConfig().LMS_BASE_URL}/courses/${openedxCourseId}/home`
          : undefined),
    };
  }), [programCourses, courseInfoById, progressByCourseId, ratingsByContentId, effortByCourseId, tagsByCourseId]);

  const total = courses.length;
  const completedCount = courses.filter((c) => c.status === 'completed').length;
  const overallPercent = total > 0
    ? Math.round(courses.reduce((sum, c) => sum + c.percent, 0) / total)
    : 0;
  const continueCourse = courses.find((c) => c.status !== 'completed' && !c.isLocked)
    || courses.find((c) => c.status !== 'completed')
    || courses[0];

  const handleLockedCourseClick = (course) => {
    const formatted = formatStartDate(course.startDate);
    const days = daysUntil(course.startDate);
    let primary;
    if (formatted && days) {
      primary = intl.formatMessage(coursesMessages.courseStartsInDays, { days, date: formatted });
    } else if (formatted) {
      primary = intl.formatMessage(coursesMessages.courseStartsOn, { date: formatted });
    } else {
      primary = intl.formatMessage(coursesMessages.courseHasNotStarted);
    }
    const message = (
      <>
        <div>{primary}</div>
        {formatted && <div>{intl.formatMessage(coursesMessages.addCalendarReminder)}</div>}
      </>
    );
    setToast({ id: Date.now(), message });
  };

  return (
    <div className="program-inner">
      <ProgramBanner
        title={program.name}
        imageUrl={resolveImage(program.image)}
        coursesLeft={total - completedCount}
      />
      <ProgramProgressCard
        completed={completedCount}
        total={total}
        percent={overallPercent}
        continueUrl={continueCourse?.homeUrl}
      />
      <ProgramAbout body={program.about || program.description} />
      <EnrolledProgramCourses
        courses={courses}
        onLockedClick={handleLockedCourseClick}
      />
      <Toast show={!!toast} variant="warning" onClose={() => setToast(null)}>
        {toast?.message}
      </Toast>
    </div>
  );
};

export default EnrolledProgramView;
